import { Injectable, Injector, inject } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import moment from 'moment';
import { firstValueFrom } from 'rxjs';
import { PlatformChartMomentExportDto } from '@data/_generated-api/platform-api/models/platform-chart-moment-export-dto';
import { PlatformChartService } from '@data/_generated-api/platform-api/services/platform-chart.service';
import { downloadText } from '@shared/utils/download-file.utils';
import { safeFileName, uniqueFileName } from '@shared/utils/markdown-export.utils';
import { momentLocaleFor } from '@shared/utils/moment-locale.utils';
import { ZipEntry, downloadZip } from '@shared/utils/zip-download.utils';
import { HdExportPlanetsService } from '../../export/hd-export-planets';
import { MdPlanetRow } from '../../export/hd-export-planets.markdown';
import { hdMoodLabelKey, hdMomentCategoryLabelKey, hdVitalityLabelKey } from '../bodygraph/advanced-properties/hd-moment-fields';
import { HdPlanetKey, getHdPlanetDisplayConfig } from '../bodygraph/planet-grid/hd-planet-display-config';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdChartGroupService } from '../library/hd-chart-group.service';
import {
  HdMomentIndexEntry,
  HdMomentMarkdownLabels,
  attachmentPath,
  buildMomentMarkdown,
  buildMomentsIndexMarkdown,
  momentFileName,
  momentStartUtc,
  shouldExportMoment,
} from './hd-moment-markdown';

/** Uitkomst van een momentexport; `count` 0 = er viel niets te exporteren. */
export interface HdMomentsExportOutcome {
  readonly count: number;
  /** De planeetstanden konden niet geladen worden; de export ging door zonder. */
  readonly planetsMissing: boolean;
  /** Aantal afbeeldingen dat niet geladen kon worden (de .md vermeldt dan enkel de naam). */
  readonly attachmentsMissing: number;
}

/**
 * Markdown-export van momenten (spec 2026-10-21-dagboek-momenten-export-design §2.3): één moment
 * als losse .md (of een kleine zip met afbeeldingen), alle momenten van een chart als zip met
 * index.md en een map attachments/. Cycles gaan enkel mee wanneer er iets over geschreven is.
 * Teksten volgen de HD-contenttaal, zoals de Momenten-sectie.
 */
@Injectable({ providedIn: 'root' })
export class HdMomentsExportService {
  private readonly chartApi = inject(PlatformChartService);
  private readonly groups = inject(HdChartGroupService);
  /** Pas bij het exporteren ophalen: de planeetservice trekt HdPlanetInfoService mee (zie de dagboekexport). */
  private readonly injector = inject(Injector);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  /** Alle momenten van een chart als zip. */
  async exportChartMoments(chartId: string, chartName: string): Promise<HdMomentsExportOutcome> {
    const result = await firstValueFrom(this.chartApi.getChartsIdMomentsExport({ id: chartId }));
    if (!result?.isValid) throw new Error(result?.simpleMessage || 'De momenten konden niet geladen worden.');
    const moments = (result.value ?? []).filter(shouldExportMoment);
    if (moments.length === 0) return { count: 0, planetsMissing: false, attachmentsMissing: 0 };
    return this.export(moments, { zipBase: `${this.translate('humanDesign.momentsExport.fileBase')} ${chartName}`, chartName });
  }

  /** Eén chart als moment: een losse .md, of een zip wanneer er afbeeldingen bij horen. */
  async exportMoment(chartId: string): Promise<HdMomentsExportOutcome> {
    const result = await firstValueFrom(this.chartApi.getChartsIdMomentExport({ id: chartId }));
    if (!result?.isValid || !result.value) throw new Error(result?.simpleMessage || 'Het moment kon niet geladen worden.');
    return this.export([result.value]);
  }

  private async export(
    moments: readonly PlatformChartMomentExportDto[],
    collection?: { readonly zipBase: string; readonly chartName: string },
  ): Promise<HdMomentsExportOutcome> {
    const lang = this.contentLang.effectiveLang();
    const locale = momentLocaleFor(lang);
    const labels = this.markdownLabels();
    const now = moment().locale(locale);
    const used = new Set<string>();
    const files: ZipEntry[] = [];
    const index: HdMomentIndexEntry[] = [];

    const planets = await this.loadPlanets(moments);
    const attachments = await this.loadAttachments(moments, used);

    moments.forEach((item, position) => {
      const startUtc = momentStartUtc(item);
      const start = startUtc ? moment.utc(startUtc).local().locale(locale) : undefined;
      const end = item.momentEndUtc ? moment.utc(item.momentEndUtc).local().locale(locale) : undefined;
      const markdown = buildMomentMarkdown({
        moment: item,
        planets: planets.rows[position] ?? [],
        attachmentPaths: attachments.paths,
        startText: start?.format('dddd D MMMM YYYY, HH:mm'),
        endText: end?.format('dddd D MMMM YYYY, HH:mm'),
        startIso: start?.format(),
        endIso: end?.format(),
        exportedText: now.format('D MMMM YYYY'),
        exportedIso: now.format(),
        labels,
      });
      const fileName = uniqueFileName(momentFileName(item, start?.format('YYYY-MM-DD')), used);
      files.push({ path: fileName, content: markdown });
      index.push({
        fileName,
        dateText: start ? start.format('D MMM YYYY') : '',
        name: item.name ?? '',
        category: item.category ? labels.category(item.category) : '',
      });
    });

    const outcome: HdMomentsExportOutcome = {
      count: moments.length,
      planetsMissing: planets.missing,
      attachmentsMissing: attachments.missing,
    };

    if (!collection && attachments.files.length === 0) {
      downloadText(files[0].content as string, files[0].path, 'text/markdown');
      return outcome;
    }

    if (collection) {
      const indexMarkdown = buildMomentsIndexMarkdown([...index].reverse(), {
        title: this.translate('humanDesign.momentsExport.md.indexTitle', { chart: collection.chartName }),
        summary: this.translate('humanDesign.momentsExport.md.indexSummary', { count: moments.length, date: now.format('D MMMM YYYY') }),
        date: this.translate('humanDesign.momentsExport.md.indexDate'),
        name: this.translate('humanDesign.momentsExport.md.indexName'),
        category: this.translate('humanDesign.momentsExport.md.indexCategory'),
      });
      files.unshift({ path: uniqueFileName('index.md', used), content: indexMarkdown });
    }
    const zipBase = collection?.zipBase ?? files[0].path.replace(/\.md$/, '');
    await downloadZip([...files, ...attachments.files], safeFileName(`${zipBase} ${now.format('YYYY-MM-DD')}`, 'zip'));
    return outcome;
  }

  private async loadPlanets(moments: readonly PlatformChartMomentExportDto[]): Promise<{ rows: MdPlanetRow[][]; missing: boolean }> {
    const times = moments.map(momentStartUtc);
    const known = times.filter((time): time is string => !!time);
    if (known.length === 0) return { rows: [], missing: false };
    try {
      const rows = await this.injector.get(HdExportPlanetsService).rowsAt(known);
      let next = 0;
      return { rows: times.map(time => (time ? rows[next++] : [])), missing: false };
    } catch {
      return { rows: [], missing: true };
    }
  }

  /** Elke bijlage (eigen notitie en linknotities) één keer laden; een laadfout telt mee maar stopt de export niet. */
  private async loadAttachments(
    moments: readonly PlatformChartMomentExportDto[],
    used: Set<string>,
  ): Promise<{ files: ZipEntry[]; paths: Map<string, string>; missing: number }> {
    const files: ZipEntry[] = [];
    const paths = new Map<string, string>();
    let missing = 0;
    for (const item of moments) {
      const startUtc = momentStartUtc(item);
      const localDate = startUtc ? moment.utc(startUtc).local().format('YYYY-MM-DD') : undefined;
      const all = [...(item.noteAttachments ?? []), ...(item.links ?? []).flatMap(link => link.noteAttachments ?? [])];
      let counter = 0;
      for (const attachment of all) {
        if (!attachment.id || paths.has(attachment.id)) continue;
        try {
          const content = await this.groups.getNoteAttachmentContent(attachment.id);
          const path = uniqueFileName(attachmentPath(item, localDate, ++counter, attachment), used);
          files.push({ path, content: base64ToBytes(content.dataBase64 ?? '') });
          paths.set(attachment.id, path);
        } catch {
          missing++;
        }
      }
    }
    return { files, paths, missing };
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate<string>(key, params, this.contentLang.effectiveLang());
  }

  private markdownLabels(): HdMomentMarkdownLabels {
    const t = (key: string, params?: Record<string, unknown>): string => this.translate(key, params);
    return {
      chartType: type => t(`enums.chartType.${type}`),
      category: key => t(hdMomentCategoryLabelKey(key)),
      mood: value => t(hdMoodLabelKey(value)),
      vitality: value => t(hdVitalityLabelKey(value)),
      emotion: key => t(`humanDesign.momentFields.emotions.${key}`),
      moodTitle: t('humanDesign.momentFields.moodLabel'),
      vitalityTitle: t('humanDesign.momentFields.vitalityLabel'),
      emotionsTitle: t('humanDesign.momentFields.emotionsLabel'),
      mindTitle: t('humanDesign.momentsExport.md.mind'),
      centerName: center => t(`humanDesign.properties.centerNames.${center}`),
      gateLabel: gate => t('humanDesign.mindLog.gate', { gate }),
      planetName: planetKey => getHdPlanetDisplayConfig(planetKey as HdPlanetKey).label,
      linkTitle: (relationType, chart) =>
        t(relationType === 'cycleOf' ? 'humanDesign.momentsExport.md.cycleOf' : 'humanDesign.momentsExport.md.momentOf', { chart }),
      notes: t('humanDesign.momentsExport.md.notes'),
      planetsAt: t('humanDesign.momentsExport.md.planetsAt'),
      planetsAtStart: t('humanDesign.momentsExport.md.planetsAtStart'),
      planets: {
        planet: t('humanDesign.momentsExport.md.planet'),
        gateLine: t('humanDesign.momentsExport.md.gateLine'),
        retrograde: t('humanDesign.momentsExport.md.retrograde'),
      },
      until: t('humanDesign.momentsExport.md.until'),
      exported: date => t('humanDesign.momentsExport.md.exported', { date }),
    };
  }
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index++) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}
